import { useState, useEffect, memo } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Species, Genus } from '../../types/plant';
import { plantApi } from '../../lib/plantApi';
import { Button, ComboBox } from '../ui';
import type { ComboBoxOption } from '../ui';
import { useSearchInput } from '../../hooks/useSearchInput';
import { useDebounce } from '../../hooks/useDebounce';

interface SpeciesSearchProps {
  value?: Species | null;
  onChange: (species: Species | null) => void;
  genus?: Genus | null;
  error?: string;
  placeholder?: string;
  onCreateNew?: (speciesName: string, genus: Genus) => void;
  disabled?: boolean;
}

function SpeciesSearchComponent({
  value,
  onChange,
  genus,
  error,
  placeholder = 'Search for a species...',
  onCreateNew,
  disabled = false,
}: SpeciesSearchProps) {
  const [species, setSpecies] = useState<Species[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const { searchValue: searchQuery, handleChange: handleSearchChange } = useSearchInput({
    onSearchChange: () => {}, // We handle the search in the useEffect below
    debounceMs: 300,
  });

  // Use debounced search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Search species when genus or debounced query changes
  useEffect(() => {
    if (!genus) {
      setSpecies([]);
      setIsSearching(false);
      return;
    }

    if (debouncedSearchQuery.trim().length >= 1) {
      setIsSearching(true);
      plantApi
        .searchSpeciesByGenus(genus.id)
        .then(allSpecies => {
          // Filter species by search query
          const filteredSpecies = allSpecies.filter(s =>
            s.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
          );
          setSpecies(filteredSpecies || []);
        })
        .catch(() => {
          setSpecies([]);
        })
        .finally(() => {
          setIsSearching(false);
        });
    } else {
      // If no search query, show all species for the genus
      setIsSearching(true);
      plantApi
        .searchSpeciesByGenus(genus.id)
        .then(allSpecies => {
          setSpecies(allSpecies || []);
        })
        .catch(() => {
          setSpecies([]);
        })
        .finally(() => {
          setIsSearching(false);
        });
    }
  }, [genus, debouncedSearchQuery]);

  // Reset value when genus changes
  useEffect(() => {
    if (value && genus && value.genus?.id !== genus.id) {
      onChange(null);
    }
  }, [genus, value, onChange]);

  // Convert species to ComboBox options
  const allSpecies = [...species];

  // Add the selected value if it's not already in the species array
  if (value && !species.some(s => s.id === value.id)) {
    allSpecies.unshift(value);
  }

  const speciesOptions: ComboBoxOption<Species>[] = allSpecies.map(spec => ({
    value: spec,
    label: spec.name,
  }));

  const renderSpeciesOption = (option: ComboBoxOption<Species>) => {
    const spec = option.value;
    return (
      <div className="flex flex-col">
        <span className="font-medium text-foreground italic">
          {genus?.name || spec.genus?.name} {spec.name}
        </span>
      </div>
    );
  };

  const handleCreateNew = () => {
    if (onCreateNew && searchQuery.trim() && genus) {
      onCreateNew(searchQuery.trim(), genus);
    }
  };

  const dropdownFooter =
    searchQuery.trim().length >= 1 && onCreateNew && genus ? (
      <div className="px-3 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleCreateNew}
          className="w-full justify-start text-green-600 hover:text-green-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create species "{genus.name} {searchQuery.trim()}"
        </Button>
      </div>
    ) : null;

  // Show disabled state when no genus is selected
  const effectiveDisabled = disabled || !genus;
  const effectivePlaceholder = !genus ? 'Select a genus first...' : placeholder;

  return (
    <ComboBox
      value={value}
      onChange={onChange}
      options={speciesOptions}
      placeholder={effectivePlaceholder}
      error={error}
      searchable
      onSearch={handleSearchChange}
      searchQuery={searchQuery}
      loading={isSearching}
      disabled={effectiveDisabled}
      emptyMessage={
        !genus
          ? 'Select a genus to search species'
          : searchQuery.trim().length >= 1
            ? `No species found for "${searchQuery}" in ${genus.name}`
            : `No species found in ${genus.name}`
      }
      renderOption={renderSpeciesOption}
      dropdownFooter={dropdownFooter}
      getOptionKey={(species: Species) => species.id}
    />
  );
}

export const SpeciesSearch = memo(SpeciesSearchComponent);
